/**
 * How a node relates to the others: the chain up its first acyclic link kind (an inheritance
 * chain, in code), then one group per link kind and direction (extends, extended by, uses,
 * used by...), each counted and cut after {@link NEIGHBOURS_SHOWN}. Each neighbour can be opened,
 * and the link to it edited or removed.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { incoming, inheritanceChain, outgoing, type Neighbour } from "../../shared/analysis";
import { toast } from "../../shared/toast";
import { Dot, Group, NodeRef } from "../../shared/ui";
import { useOpenNode } from "./openNode";

/** How many neighbours a list shows before "Show all". */
export const NEIGHBOURS_SHOWN = 8;

/** A list of neighbours, each with its link's edit and remove buttons; a long one is cut, with "Show all". */
function Neighbours(props: { list: Neighbour[] }): JSX.Element {
  const { actions, view } = useApp();
  const open = useOpenNode();
  const [all, setAll] = createSignal(false);
  const shown = () => (all() ? props.list : props.list.slice(0, NEIGHBOURS_SHOWN));
  return (
    <>
      <For each={shown()}>
        {(n) => (
          <NodeRef
            node={n.node}
            onOpen={() => open(n.node.id)}
            onEditLink={() => view.selectLink(n.link)}
            onRemoveLink={() => void actions.removeLink(n.link).then((ok) => ok && toast("Link removed."))}
          />
        )}
      </For>
      <Show when={!all() && props.list.length > NEIGHBOURS_SHOWN}>
        <li>
          <button type="button" class="linklike small" data-show-all onClick={() => setAll(true)}>
            Show all {props.list.length}
          </button>
        </li>
      </Show>
    </>
  );
}

/** The parents of a node along the first acyclic link kind (`extends`), from the closest to the root. */
export function InheritanceChain(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { graph, kinds } = useApp();
  const open = useOpenNode();
  /** The link kind followed: the first acyclic one. */
  const along = () => kinds.links().find((k) => k.tone === "hierarchy");
  const chain = createMemo(() => {
    const kind = along();
    return kind ? inheritanceChain(graph.state.graph, n().id, kind.id) : [];
  });
  return (
      <Show when={chain().length}>
        <section class="grp">
          <h3>
            {along()?.label} chain <span>{chain().length + 1}</span>
          </h3>
          <ol class="chain">
            <li class="self" style={{ "--d": 0 }}>
              <button type="button" class="ref">
                <Dot node={n()} />
                <span>{n().name}</span>
              </button>
            </li>
            <For each={chain()}>
              {(c, i) => (
                <li style={{ "--d": i() + 1 }}>
                  <button type="button" class="ref" onClick={() => open(c.id)}>
                    <Dot node={c} />
                    <span>{c.name}</span>
                    <em>{kinds.ofNode(c).label}</em>
                  </button>
                </li>
              )}
            </For>
          </ol>
        </section>
      </Show>
  );
}

/** The neighbour groups. */
export function NodeNeighbours(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { graph, kinds } = useApp();
  /** The whole graph. */
  const g = () => graph.state.graph;
  /** Every link kind this node has links of, defined ones first, each with its two lists. */
  const groups = createMemo(() => {
    const id = n().id;
    const touching = g().links.filter((l) => l.from === id || l.to === id).map((l) => l.kind);
    const ids = [...kinds.links().map((k) => k.id), ...touching].filter((k, i, all) => touching.includes(k) && all.indexOf(k) === i);
    return ids.map((kind) => ({ kind: kinds.ofLink({ kind }), out: outgoing(g(), id, kind), in: incoming(g(), id, kind) }));
  });
  return (
    <For each={groups()}>
      {(group) => (
        <>
          <Group title={group.kind.label} count={group.out.length}>
            <Neighbours list={group.out} />
          </Group>
          <Group title={group.kind.inverse} count={group.in.length}>
            <Neighbours list={group.in} />
          </Group>
        </>
      )}
    </For>
  );
}
